/**
 * Hook to delete images from the database globally
 * Deleting an image removes it from all galleries
 */

import { useState } from "react";
import { toast } from "@/lib/toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useGeneratedImagesStore } from "@/contexts/GeneratedImagesContext";
import { deleteGeneratedImageById, deleteGeneratedImageByUrl, deleteGeneratedImagesByIds } from "@/services/db/generatedImages";
import { deleteLikedMediaByUrl } from "@/services/db/likedMedia";

export function useImageDelete() {
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useLanguage();
  const store = useGeneratedImagesStore();

  const deleteImage = async (imageId: string): Promise<boolean> => {
    if (!imageId || isDeleting) return false;

    setIsDeleting(true);
    const snapshot = store.optimisticRemoveById(imageId);

    try {
      await deleteGeneratedImageById(imageId);

      toast.success(t.imageDelete.imageDeleted, {
        description: t.imageDelete.removedFromGalleries,
      });

      return true;
    } catch (error) {
      console.error("Error deleting image:", error);
      store.rollbackRemoval(snapshot);
      toast.error(t.imageDelete.deleteFailed, {
        description: error instanceof Error ? error.message : String(error),
      });
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteImageByUrl = async (imageUrl: string): Promise<boolean> => {
    if (!imageUrl || isDeleting) return false;

    setIsDeleting(true);
    const snapshot = store.optimisticRemoveByUrl(imageUrl);

    try {
      await deleteGeneratedImageByUrl(imageUrl);

      // Also try to delete from liked_media if it exists there
      await deleteLikedMediaByUrl(imageUrl);

      toast.success(t.imageDelete.imageDeleted, {
        description: t.imageDelete.removedFromGalleries,
      });

      return true;
    } catch (error) {
      console.error("Error deleting image:", error);
      store.rollbackRemoval(snapshot);
      toast.error(t.imageDelete.deleteFailed, {
        description: t.imageDelete.deleteError,
      });
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteFailedImages = async (ids: string[]): Promise<boolean> => {
    if (ids.length === 0 || isDeleting) return false;

    setIsDeleting(true);
    const snapshots = ids.map((id) => store.optimisticRemoveById(id));

    try {
      await deleteGeneratedImagesByIds(ids);
      toast.success(t.imageDelete.imageDeleted);
      return true;
    } catch (error) {
      console.error("Error deleting failed images:", error);
      snapshots.forEach((snapshot) => store.rollbackRemoval(snapshot));
      toast.error(t.imageDelete.deleteFailed, {
        description: error instanceof Error ? error.message : String(error),
      });
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    deleteImage,
    deleteImageByUrl,
    deleteFailedImages,
    isDeleting,
  };
}
